/**
 * Re-export of the DS status pill. Source lives in
 * `design-system/registry/status-pill.tsx`; we adapt only the `cn` import
 * so pulse-v2's utility path (`@/lib/cn`) resolves without touching the
 * vendored file.
 */
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

/**
 * Solid fill, light text: the tone colour is the block and the label sits in
 * its `-soft` pair, so a status reads at a glance from across the row. 4px
 * corners, not a pill, so it stays visually distinct from the rounded-full
 * filter chips and Preferred badges around it.
 */
const statusPillVariants = cva(
  'inline-flex shrink-0 items-center rounded-[4px] px-1.5 py-px text-[11px] font-medium leading-4 whitespace-nowrap',
  {
    variants: {
      tone: {
        ok: 'bg-ok text-ok-soft',
        warn: 'bg-warn text-warn-soft',
        crit: 'bg-crit text-crit-soft',
        mute: 'bg-[color:var(--muted-foreground)] text-mute-soft',
      },
    },
    defaultVariants: { tone: 'mute' },
  },
)

export type StatusTone = NonNullable<VariantProps<typeof statusPillVariants>['tone']>

export function StatusPill({
  className,
  tone,
  children,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof statusPillVariants>) {
  return (
    <span
      data-slot="status-pill"
      className={cn(statusPillVariants({ tone }), className)}
      {...props}
    >
      {children}
    </span>
  )
}

export { statusPillVariants }
